import axios from 'axios';

import config from '../config/config.json';

const api = axios.create({
  baseURL: config.apiUrl,
  maxContentLength: 50 * 1024 * 1024,
  timeout: 15000,
});

// ====================================================================================
// Proteção contra "tempestade de 401"
// ------------------------------------------------------------------------------------
// Quando o token expira com várias requisições em voo, TODAS voltam 401. Sem esse
// controle, cada 401 dispara um novo ciclo de re-login (ErrorProcessor), e cada login
// custa um bcrypt no servidor — dezenas de requisições viram dezenas de logins.
// Aqui a sessão é marcada como inválida no primeiro 401: as requisições seguintes são
// rejeitadas localmente, sem sair para a rede, até que o login seja refeito.
// ====================================================================================

// Rotas que precisam passar mesmo com a sessão marcada como inválida.
const SESSION_RECOVERY_PATHS = ['/tokens'];

// Prazo máximo de bloqueio. Existe apenas como rede de segurança: se por algum motivo
// o login não acontecer, a aplicação volta a funcionar em vez de ficar travada.
const SESSION_GUARD_TIMEOUT = 10000;

let sessionUnauthorized = false;
let sessionGuardTimer = null;

const isRecoveryRequest = (url = '') =>
  SESSION_RECOVERY_PATHS.some((path) => String(url).includes(path));

export const isSessionUnauthorized = () => sessionUnauthorized;

export const resetSessionGuard = () => {
  sessionUnauthorized = false;
  if (sessionGuardTimer) {
    clearTimeout(sessionGuardTimer);
    sessionGuardTimer = null;
  }
};

const markSessionUnauthorized = () => {
  if (sessionUnauthorized) return;
  sessionUnauthorized = true;
  sessionGuardTimer = setTimeout(resetSessionGuard, SESSION_GUARD_TIMEOUT);
};

api.interceptors.request.use((requestConfig) => {
  if (sessionUnauthorized && !isRecoveryRequest(requestConfig.url)) {
    const error = new Error('Sessão expirada: requisição cancelada.');
    error.isSessionExpired = true;
    error.config = requestConfig;
    return Promise.reject(error);
  }
  return requestConfig;
});

api.interceptors.response.use(
  (response) => {
    // Login refeito com sucesso: libera a fila novamente.
    if (isRecoveryRequest(response.config && response.config.url)) {
      resetSessionGuard();
    }
    return response;
  },
  (error) => {
    const status = error.response && error.response.status;
    const url = error.config && error.config.url;

    if (status === 401 && !isRecoveryRequest(url)) {
      markSessionUnauthorized();
    }

    return Promise.reject(error);
  }
);

export default api;
